import React, { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Truck, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = {
  delivery: { list: "/deliveries", attach: "/route-tasks/attach-delivery", title: "Adjuntar nota de envío", perm: "deliveries:view" },
  invoice: { list: "/invoices", attach: "/route-tasks/attach-invoice", title: "Adjuntar factura de venta", perm: "invoices:print" },
};

const label = (kind, r) => `${r.number}${kind === "invoice" && r.fel_series ? ` (${r.fel_series}-${r.fel_number})` : ""} · ${r.client_name || ""} · ${r.date || ""} · ${money(r.currency || "GTQ", r.total || 0)}`;

// Adjunta el PDF de una nota de envío o factura existente a la tarea de ruta.
export const TaskDocAttach = ({ clientId, onAttached }) => {
  const { can } = useAuth();
  const [kind, setKind] = useState(null);
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!kind) { return; }
    setPick(""); setRows([]);
    api.get(KINDS[kind].list, { params: { page: 0 } }).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setRows(list.filter((x) => x.status !== "Anulada" && (!clientId || x.client_id === clientId)));
    }).catch(() => setRows([]));
  }, [kind, clientId]);

  const attach = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${KINDS[kind].attach}/${pick}`);
      onAttached(data, rows.find((x) => x.id === pick)?.number);
      toast.success("Documento adjuntado"); setKind(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (<>
    <div className="grid grid-cols-2 gap-2 mt-2">
      {can("deliveries:view") && <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setKind("delivery")} data-testid="task-attach-delivery-btn"><Truck className="h-3.5 w-3.5" /> Nota de envío</Button>}
      {can("invoices:print") && <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setKind("invoice")} data-testid="task-attach-invoice-btn"><Receipt className="h-3.5 w-3.5" /> Factura de venta</Button>}
    </div>
    <Dialog open={!!kind} onOpenChange={(o) => !o && !busy && setKind(null)}>
      <DialogContent className="rounded-sm max-w-md" data-testid="task-attach-doc-dialog">
        <DialogHeader><DialogTitle>{kind && KINDS[kind].title}</DialogTitle>
          <DialogDescription>{clientId ? "Mostrando documentos del cliente de la tarea." : "Elige el cliente en la tarea para filtrar sus documentos."} Se adjunta el PDF.</DialogDescription></DialogHeader>
        <SearchableSelect value={pick} onChange={setPick} testid="task-attach-doc-pick" placeholder={rows.length ? "Selecciona..." : "Sin documentos"} searchPlaceholder="Buscar número o cliente..."
          options={rows.map((r) => ({ value: r.id, label: label(kind, r) }))} />
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => setKind(null)}>Cancelar</Button>
          <Button disabled={!pick || busy} onClick={attach} data-testid="task-attach-doc-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adjuntar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
};
